import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.dh{justify-content:flex-end;}.ssr-plugin'
import '.c1{align-items:flex-start;}.ssr-plugin'
import '.av{width:fit-content;}.ssr-plugin'
import '.ck{max-width:100%;}.ssr-plugin'
import '.ec{height:auto;}.ssr-plugin'
import '.bl{flex-shrink:0;}.ssr-plugin'
import '.ju{display:block;}.ssr-plugin'
import '.jv{margin-top:0.25rem;}.ssr-plugin'
import '.jw{padding-right:2rem;}.ssr-plugin'
import '.jx{align-self:flex-start;}.ssr-plugin'
import '.jy{margin-top:auto;}.ssr-plugin'
import { useId } from 'react'
import { Style, styled } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { TextLink } from '@app/components/core/link/TextLink'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useTranslations } from '@i18n/useLanguage'
import { expandImage, type StoredImage } from '@app/types/StoredImage'
import useMediaQuery from '@app/util/useMediaQuery'
import { resizeImage } from '@app/util/helpers'
import Column from '@app/components/layout/Column'

const cardVersions = {
  accentColored: {
    background: 'var(--brand-accent)',
    imageCutout: {
      path: 'M271.38 71.5771L300.087 146.295C301.152 149.068 300.835 152.177 299.23 154.683L181.084 339.216C178.267 343.616 172.375 344.925 167.925 342.14L-59.7114 199.69C-64.1654 196.91 -65.4895 191.086 -62.6722 186.685L54.0422 4.38905C56.3844 0.730886 60.9396 -0.880774 65.0954 0.477717L265.461 65.9762C268.189 66.8665 270.359 68.9267 271.38 71.5771Z',
      viewBox: '0 0 301 185',
      width: 301,
      height: 185,
    },
  },
  tertiaryColored: {
    background: 'var(--brand-tertiary)',
    imageCutout: {
      path: 'M271.062 126.841C241.119 93.7519 216.24 88.4694 189.009 100.052C183.236 102.51 177.239 97.2125 179.079 91.2672C184.874 72.5652 179.31 53.1931 157.439 29.0249C107.897 -25.7225 73.8084 4.42818 13.4035 57.8546L-39.2391 104.416C-99.6439 157.842 -133.733 187.993 -84.1906 242.74C-62.3203 266.908 -43.4584 274.529 -24.0204 270.903C-17.8398 269.754 -13.1841 276.229 -16.2899 281.634C-30.9532 307.105 -28.3966 332.14 1.54189 365.224C56.7988 426.287 94.8226 392.656 162.203 333.06L170.472 325.746C173.717 322.876 178.767 323.427 181.3 326.924L203.992 358.263C206.525 361.76 211.575 362.311 214.82 359.441L241.605 335.75C244.85 332.88 244.856 327.857 241.622 324.98L212.635 299.21C209.401 296.333 209.406 291.31 212.651 288.44L220.92 281.126C288.3 221.53 326.324 187.899 271.067 126.836L271.062 126.841Z',
      viewBox: '0 0 298 195',
      width: 298,
      height: 195,
    },
  },
  lightBlueColored: {
    background: 'var(--palette-blue-100)',
    imageCutout: {
      path: 'M277.778 110.043L281.789 97.2787C284.071 90.0137 279.962 82.3018 272.621 80.0474L220.202 63.949C212.854 61.6922 208.755 53.9748 211.035 46.7177L215.046 33.9533C217.328 26.6883 213.219 18.9764 205.878 16.722L153.459 0.623584C146.111 -1.63321 138.31 2.42954 136.03 9.68667L132.019 22.4511C129.737 29.716 121.93 33.7686 114.59 31.5143L62.1711 15.4159C54.8227 13.1591 47.022 17.2218 44.7417 24.4789L40.731 37.2433C38.4483 44.5083 30.6421 48.5609 23.3016 46.3065L-29.1172 30.2081C-36.4657 27.9513 -44.2663 32.0141 -46.5466 39.2712L-136.467 325.448C-138.749 332.713 -134.64 340.425 -127.299 342.679L241.182 455.844C248.53 458.1 256.331 454.038 258.611 446.78L348.531 160.604C350.814 153.339 346.705 145.627 339.364 143.373L286.945 127.274C279.597 125.018 275.498 117.3 277.778 110.043Z',
      viewBox: '0 0 350 184',
      width: 350,
      height: 184,
    },
  },
}

export type StoryCardVersion = keyof typeof cardVersions

export type StoryCardProps = {
  title: string
  subtitle?: string
  to: string
  image?: string | StoredImage
  version: StoryCardVersion | number
  style?: Style
}

const StoryCardImage = ({
  src,
  alt,
  clipId,
  version,
}: {
  src: string
  alt: string
  clipId: string
  version: StoryCardVersion
}) => {
  const isMobile = useMediaQuery()
  const resizedSrc = resizeImage(src, isMobile ? 400 : 600, 'w', isMobile)
  const cutout = cardVersions[version].imageCutout

  return (
    <div className="d e dh c1 av ck"
    >
      <svg className="ck ec bl ju"
        viewBox={cutout.viewBox}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{
          width: `${cutout.width / 16}rem`,}}
      >
        <defs>
          <clipPath id={clipId}>
            <path d={cutout.path} />
          </clipPath>
        </defs>
        <styled.image
          href={resizedSrc}
          width={cutout.width}
          height={cutout.height}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
          aria-label={alt}
        />
      </svg>
    </div>
  )
}

export const StoryCard = ({
  title,
  subtitle,
  to,
  image,
  version,
  style,
}: StoryCardProps) => {
  const t = useTranslations().app.common
  const isMobile = useMediaQuery()
  const clipId = useId()
  const { src, altText } = expandImage(image, title)
  const versionKeys = Object.keys(cardVersions)
  const versionKey =
    typeof version === 'number'
      ? (versionKeys[version % versionKeys.length] as StoryCardVersion)
      : version

  return (
    <Column
      data-testid="story-card"
      style={{
        gap: isMobile ? '1.5rem' : '2rem',
        borderRadius: '1.25rem',
        alignItems: 'stretch',
        overflow: 'hidden',
        backgroundColor: cardVersions[versionKey].background,
        ...style,
      }}
    >
      <Column
        style={{
          gap: '0.75rem',
          flex: '1 1 auto',
          minWidth: 0,
          padding: '2rem',
        }}
      >
        <Title.H3 style={{ margin: 0 }}>{title}</Title.H3>
        {subtitle && (
          <Text.Medium style={{ margin: 0 }}>{subtitle}</Text.Medium>
        )}
      </Column>

      <div className="d dh jv jw"
      >
        <TextLink
          to={to}
          size="large"
          isBold={true}
          icon={faArrowRight}
          iconPosition="right"
          containerStyle={{
            textDecoration: 'none',
          }}
        >
          {t.readMore}
        </TextLink>
      </div>

      {src && (
        <div className="jx jy"
        >
          <StoryCardImage
            src={src}
            alt={altText}
            clipId={clipId}
            version={versionKey}
          />
        </div>
      )}
    </Column>
  )
}
